import { useEffect, useState } from "react";
import { useProjectStore } from "../../store/useProjectStore";
import { useClientStore } from "../../store/useClientStore";
import { Pencil, Trash2, Loader2, Eye } from "lucide-react";
import ProjectViewModal from "./ProjectViewModal";

interface ProjectListProps {
  setEditingProject: (p: any) => void;
}

const ProjectList = ({ setEditingProject }: ProjectListProps) => {
  const { projects, fetchProjects, deleteProject, isFetchingProjects, isDeletingProject } = useProjectStore();
  const {  fetchClients } = useClientStore();
  // Track by id and derive from the store so the open View modal live-updates
  // when a socket-triggered refetch brings fresh project/milestone data.
  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewingProject = viewingId ? projects.find((p) => p._id === viewingId) || null : null;

  useEffect(() => {
    fetchProjects();
    fetchClients();
  }, []);

  // Live-refresh when ERP pushes a project/milestone update (instant via socket).
  useEffect(() => {
    const onSync = () => fetchProjects(true);
    window.addEventListener("cre:project-sync", onSync);
    return () => window.removeEventListener("cre:project-sync", onSync);
  }, [fetchProjects]);

  // Reliable fallback: silently refetch every 12s while the tab is visible, so
  // ERP → CRE status/milestone updates appear without a manual reload even if the
  // live socket signal doesn't arrive (e.g. multi-instance production).
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchProjects(true);
    }, 12000);
    return () => clearInterval(id);
  }, [fetchProjects]);

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm("Are you sure you want to delete this project?");
    if (!confirmed) return;
    await deleteProject(id);
  };

  if (isFetchingProjects) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Loader2 className="animate-spin text-[#2462b2]" size={40} />
        <p className="text-gray-500 font-medium">Loading projects...</p>
      </div>
    );
  }

  if (!projects.length) {
    return (
      <div className="text-center py-16">
        <div className="bg-gray-100 border-2 border-dashed rounded-xl w-20 h-20 mx-auto mb-4 flex items-center justify-center">
          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-lg font-semibold text-gray-700">No projects yet</p>
        <p className="text-sm text-gray-500 mt-1">Create your first project to get started!</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto cre-scroll rounded-t-2xl border border-gray-100">
         <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-gradient-to-r from-[#2462b2]/5 to-[#1e55a0]/5 text-xs font-bold uppercase tracking-wider text-gray-700 border-b border-gray-200">
            <tr>
              {["Project Name", "Assigned Contact", "Category", "Subcategory", "Supervisor", "Status", "ETA", "Payment Status", "Escrow Amount","Released Amount" , "Actions"].map((header) => (

                <th key={header} className="px-5 py-4 font-semibold">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {projects.map((project) => (
              <tr
                key={project._id}
                className="hover:bg-gradient-to-r hover:from-[#2462b2]/5 hover:to-transparent transition-all duration-200 group"
              >
                <td className="px-5 py-4 font-semibold text-gray-900">
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    {project.name}
                    {project.source === "Invitation" && (
                      <span className="px-1.5 py-px rounded-full text-[9px] font-bold tracking-wide bg-violet-100 text-violet-700">INVITED</span>
                    )}
                    {project.source === "Direct" && (
                      <span className="px-1.5 py-px rounded-full text-[9px] font-bold tracking-wide bg-amber-100 text-amber-700">DIRECT</span>
                    )}
                  </span>
                </td>
                <td className="px-5 py-4">
                  {typeof project.assignedContact === "object" ? project.assignedContact?.name || "—" : "—"}
                </td>
                <td className="px-5 py-4">{project.projectType || "—"}</td>
                <td className="px-5 py-4">{project.projectSubType || project.designType || "—"}</td>
                <td className="px-5 py-4">{project?.supervisor?.fullName || "—"}</td>
                <td className="px-5 py-4">
                 {project.isDraft ? (
  <span
    className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-bold rounded-full whitespace-nowrap bg-amber-100 text-amber-700 border border-amber-200"
    title="Saved draft — open it to continue project creation"
  >
    DRAFT
  </span>
) : (
 <span
  className={`inline-flex items-center justify-center px-3 py-1.5 text-xs font-bold rounded-full whitespace-nowrap transition-all duration-200 ${
    project.projectStatus === "Completed"
      ? "bg-emerald-100 text-emerald-700"
      : project.projectStatus === "In Progress"
      ? "bg-blue-100 text-blue-700"
      : project.projectStatus === "Cancelled"
      ? "bg-red-100 text-red-700"
      : "bg-gray-100 text-gray-700"
  }`}
>
  {project.projectStatus}
</span>
)}

                </td>
                <td className="px-5 py-4">
                  {project.eta ? new Date(project.eta).toLocaleDateString() : "—"}
                </td>
                <td className="px-5 py-4">{project.paymentStatus || "—"}</td>
                <td className="px-5 py-4">{project.escrowAmount || "—"}</td>
                <td className="px-5 py-4">{project.releasedPayment || "—"}</td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => setViewingId(project._id)}
                      className="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 transition-all duration-200 group"
                      title="View"
                    >
                      <Eye size={16} className="group-hover:scale-110 transition-transform" />
                    </button>
                    <button
                      onClick={() => setEditingProject(project)}
                      className="p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 transition-all duration-200 group"
                      title="Edit"
                    >
                      <Pencil size={16} className="group-hover:scale-110 transition-transform" />
                    </button>
                    <button
                      onClick={() => handleDelete(project._id)}
                      disabled={isDeletingProject}
                      className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 transition-all duration-200 group disabled:opacity-50"
                      title="Delete"
                    >
                      {isDeletingProject ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} className="group-hover:scale-110 transition-transform" />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewingProject && (
        <ProjectViewModal
          project={viewingProject}
          onClose={() => setViewingId(null)}
          onEdit={() => {
            const p = viewingProject;
            setViewingId(null);
            setEditingProject(p);
          }}
        />
      )}
    </>
  );
};

export default ProjectList;
